/* global functionality */
import React, { useContext } from "react";
import ProfileContext from "common/ProfileContext";
import styled from "styled-components";
import { Logo } from "./Logo";
import { Username } from "common/Username";
import { MenuGroup } from "./MenuGroup";
import { MenuItem } from "./MenuItem";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GeminiContext from "common/chat/GeminiContext";

const Aside = styled.aside`
  top: 0px;
  height: 100%;
  box-shadow: none;
  -moz-transition: all 0.5s;
  -o-transition: all 0.5s;
  -webkit-transition: all 0.5s;
  transition: all 0.5s;
  font-family: "Roboto", sans-serif;
  background: var(--sidebar-bg-color);
  width: 300px;
  overflow: hidden;
  display: inline-block;
  position: fixed;
  left: 0;
  -webkit-box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
  -moz-box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
  -ms-box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);

  a{
    text-decoration: none;
  }

  .menu{
    scrollbar-gutter: revert;
    position: relative;
    overflow-y: auto;
    height: 90vh;
    padding: 15px;

    ::-webkit-scrollbar {
      background: var(--scrollbar-color);
      width: 4px;
    }

    ::-webkit-scrollbar-thumb {
        background: var(--scrollbar-thumb-color);
    }
  }

  .menu .list {
    list-style: none;
    padding-left: 0;
    margin-bottom: 120px;

    li.menu-item.active {
      a, a:hover {
        background-color: var(--Secondary);
        margin-left: 0;
        color: var(--brand-primary);
        font-weight: 700;

        span{
          color: var(--brand-primary);
        }
      }
    }

    .ml-menu{
      height: 0px;
      overflow: hidden;
      list-style: none;
      padding-left: 0;

      &.open{
        height: auto;
      }

      li a {
        padding: 7px 0px 7px 65px;
        font-weight: 100;
        font-size: 13px;
        
        &.menu-toggle {
            padding: 7px 0px 7px 55px;
        }
      }

      li.menu-item.active {
        a, a:hover {
          background-color: var(--Secondary);
          margin-left: 0;
          color: var(--brand-primary);
          font-weight: 700;
        }


        .menu-bar {
          opacity: 0.5;
          background: var(--Primary, #fc3136);
        }
      }
        span{
          margin-left: 0;
        }
      }
    }

    a{
      padding: 5px 13px;
      border-top: var(--sidebar-border);
      color: #747474;
      position: relative;
      display: inline-flex;
      vertical-align: middle;
      width: 100%;
      border-radius: 5px;
      cursor: pointer;

      span{
        margin: 5px 0 0 12px;
        color: var(--Text-Secondary);
        font-family: "AllianceNo2-SemiBold";
        font-size: 13px;
        overflow: hidden;
        font-weight: 100;
        transition: color 0.2s;
      }

      i{
        transition: color 0.2s;
      }

      &:hover{
        background-color: var(--background-menu-hover);
        color: var(--text-primary);
      }
    }
  }

  .menu-toggle{
    &.active {
      span,
      i {
        color: var(--brand-primary);
      }

      &.link{
        background-color: var(--Secondary);
      }
    }
  }

  .menu-toggle:not(.link):not(.open-modal):not(.click-handler){
    &.toggled {
      &:after{
        transform: scale(1);
      }

      &:before{
        transform: scale(0);
      }
    }

    &:before {
      content: url(/images/caret-down.svg);
      position: absolute;
      top: calc(50% - 14px);
      right: 17px;
      font-size: 19px;
      transition: all 0.3s;
    }

    :after {
      content: url(/images/caret-up.svg);
      position: absolute;
      top: calc(50% - 14px);
      right: 17px;
      font-size: 19px;
      transform: scale(0);
      transition: all 0.3s;
    }
  }

  .menu.dark .menu-toggle:not(.link):not(.open-modal):not(.click-handler):before{
    content: url(/images/caret-down-dark.svg);
  }

  .menu.dark .menu-toggle:not(.link):not(.open-modal):not(.click-handler):after{
    content: url(/images/caret-up-dark.svg);
  }

  .menu-bar{
    display: block;
    width: 4px;
    height: 13px;
    border-radius: 1px;
    opacity: 0.2;
    background-color: var(--text-primary, #161618);
    margin-top: 1px;
    margin-right: 8px;
  }

  svg {
    font-size: 24px;
  }
`;

const BottomMenu = styled.div`
  position: fixed;
  bottom: 0px;
  left: 0px;
  width: 280px;
  background: var(--sidebar-bg-color);
  overflow: hidden;
  cursor: pointer;
  margin-left: 10px;
  z-index: 99;
  max-height: calc(100% - 120px);
  transition: left 0.5s;

  .expand-icon {
    width: 15px;
  }

  .material-icons {
    color: #747474;
  }
`;

export default function SidebarMenu() {
  const profile = useContext(ProfileContext);
  const { setOpen } = useContext(GeminiContext);

  if (!profile) {
    return null;
  }

  const isDark = window.login.isTheme("dark");

  return (
    <>
      <Aside className="sidebar-menu">
        <Logo platform={profile.platform} />

        <div className={`menu${isDark ? " dark" : ""}`}>
          <ul className="list">
            <MenuItem
              link="/viewDashboard"
              routeMatch={new RegExp("view(Cluster)?Dashboard")}
              label={<span>Dashboard</span>}
              icon="dashboard"
              menuBar={false}
            />
            <MenuGroup label="Status" icon="visibility">
              <MenuGroup label="System">
                <MenuItem link="/viewStatusSystemCPU" label="CPU" />
                <MenuItem link="/viewStatusSystemMemory" label="Memory" />
                <MenuItem link="/viewStatusSystemDisk" label="Disk" />
                <MenuItem
                  link="/viewStatusSystemInfo"
                  label="System Information"
                />
              </MenuGroup>

              <MenuGroup label="Interfaces">
                <MenuItem
                  link="/viewStatusInterfacesLinkState"
                  label="Link State"
                />
                <MenuItem
                  link="/viewStatusInterfacesThroughput"
                  label="Throughput"
                />
                <MenuItem
                  link="/viewStatusInterfacesDataWires"
                  label="Data Wires"
                />
                <MenuItem link="/viewBypass" label="Bypass" />
              </MenuGroup>

              <MenuGroup label="TCP Optimization" link="/viewStatusTCPO" />

              <MenuGroup label="Flows">
                <MenuItem link="/viewFlowsPerSubs" label="Details" />
                <MenuItem link="/viewFlowsPerProtocol" label="Per Protocol" />
              </MenuGroup>

              <MenuGroup label="Subscribers">
                <MenuItem link="/viewStatusSubscribers" label="QoE Metrics" />
                <MenuItem
                  link="/viewSubscriberThroughput"
                  label="Live Throughput"
                />
                <MenuItem
                  link="/viewSubscriberAttributes"
                  label="Subscriber Attributes"
                />
                <MenuItem
                  link="/viewSubscriberQuotas"
                  label="Subscriber Quotas"
                />
                <MenuItem
                  link="/viewStatusSubscriberGroups"
                  label="Subscriber Groups"
                />
              </MenuGroup>

              <MenuGroup label="Policies">
                <MenuItem link="/viewFlowPolicies" label="Flow Policies" />
                <MenuItem link="/viewRatePolicies" label="Rate Policies" />
                <MenuItem
                  link="/viewMonitoringPolicies"
                  label="Monitoring Policies"
                />
              </MenuGroup>

              <MenuGroup link="/viewStatusBilling" label="Billing" />
            </MenuGroup>

            <MenuGroup label="Statistics" icon="insert_chart">
              <MenuGroup label="System">
                <MenuItem link="/viewSysStatsCpu" label="CPU" />
                <MenuItem link="/viewSysStatsMem" label="Memory" />
                <MenuItem link="/viewLatencyStats" label="Latency" />
                <MenuItem link="/viewRtxStats" label="Retransmissions" />
              </MenuGroup>

              <MenuGroup label="Throughput">
                <MenuItem link="/viewBtaStatsTraffic" label="Overview" />
                <MenuItem
                  link="/viewSubscriberThroughput"
                  label="Live Subscribers"
                />
                <MenuItem link="/viewThroughputProtocols" label="Protocols" />
                <MenuItem link="/viewIfaceStkStats" label="Interfaces" />
                <MenuItem link="/viewThroughputPerPolicy" label="Policies" />
              </MenuGroup>

              <MenuGroup label="Congestion Metrics" link="/viewCongestionACM" />

              <MenuGroup label="TCP Optimization">
                <MenuItem link="/viewTCPOptimizationSpeed" label="TCP Speed" />
                <MenuItem
                  link="/viewTCPOptimizationAcceleration"
                  label="TCP Acceleration"
                />
              </MenuGroup>

              <MenuGroup label="Flows">
                <MenuItem link="/viewFlowsPerPolicy" label="Per Policy" />
                <MenuItem link="/viewBtaStatsSess" label="Per Protocol" />
              </MenuGroup>

              <MenuGroup label="Subscribers">
                <MenuItem
                  link="/viewStatusSubscriberGroups"
                  label="Subscriber Groups"
                />
                <MenuItem
                  link="/viewSubscriberThroughput"
                  label="Live Throughput"
                />
                <MenuItem link="/viewSubscribersPerPolicy" label="Per Policy" />
                <MenuItem
                  link="/viewSubscribersHourlyVolume"
                  label="Top by Time"
                />
                <MenuItem
                  link="/viewSubscribersTotalVolume"
                  label="Top Total"
                />
              </MenuGroup>
              {window.functionality.isEnabled("dpi") ? (
                <MenuGroup label="DPI Analysis">
                  <MenuItem
                    link="/viewDPIHourlyVolume"
                    label="Hourly Volume per Application"
                  />
                  <MenuItem
                    link="/viewDPITotalVolume"
                    label="Total Volume per Application"
                  />
                  <MenuItem
                    link="/viewDPILatency"
                    label="Latency per Application"
                  />
                  <MenuItem
                    link="/viewDPIServiceMetrics"
                    label="Application Speed Metrics"
                  />
                </MenuGroup>
              ) : null}
              {window.functionality.isEnabled("compression") ? (
                <MenuGroup label="HTTP Compression">
                  <MenuItem
                    link="/viewHTTPCompressionHourlyVolumeAndRatio"
                    label="Volume & Ratio"
                  />
                </MenuGroup>
              ) : null}
              <MenuGroup label="DoS Attacks">
                <MenuItem
                  link="/viewDosStatsTime"
                  label="DoS Attacks Over Time"
                />
                <MenuItem link="/viewDoSSynStats" label="SYN Attacks" />
                <MenuItem link="/viewDoSVolumeStats" label="Volume Attacks" />
              </MenuGroup>
            </MenuGroup>

            <MenuGroup
              label="Configuration"
              icon="settings"
              className="hidden-to-operators-with-hidden-rate"
            >
              <MenuGroup label="Interfaces">
                <MenuItem link="/viewInterfacesAllConf" label="All" />
                <MenuItem
                  link="/viewManagementInterfaceConf"
                  label="Management"
                />
                <MenuItem
                  link="/viewManagementFirewallConf"
                  label="Management Firewall"
                />
                <MenuItem
                  link="/viewInterfacesDataWiresConf"
                  label="Data Wires"
                />
                <MenuItem link="/viewInterfacesBypassConf" label="Bypass" />
              </MenuGroup>

              <MenuGroup label="Profiles">
                <MenuItem
                  link="/viewInterfaceProfileConfig"
                  label="Interface"
                />
                <MenuItem link="/viewVLANProfileConfig" label="VLAN" />
                <MenuItem
                  link="/viewPolicyRateProfileConfig"
                  label="Policy-rate"
                />
                <MenuItem link="/viewInternetProfileConfig" label="Internet" />
                <MenuItem
                  link="/viewSubscriberGroupProfileConfig"
                  label="Subscriber Group"
                />
                <MenuItem
                  link="/viewSubscriberIDProfileConfig"
                  label="Subscriber ID"
                />
                <MenuItem link="/viewAccessProfileConfig" label="Access" />
                <MenuItem link="/viewTimeProfileConfig" label="Time" />
                <MenuItem
                  link="/viewThroughputProfileConfig"
                  label="Throughput"
                />
                <MenuItem link="/viewDSCPProfileConfig" label="DSCP" />
                <MenuItem link="/viewDPIProfileConfig" label="DPI" />
              </MenuGroup>

              <MenuGroup link="/viewRulesFlow" label="Subscriber Flows" />

              <MenuGroup link="/viewRulesRate" label="Subscriber Rates" />

              <MenuGroup
                link="/viewRulesMonitor"
                label="Subscriber Monitoring"
              />

              <MenuGroup
                link="/viewSubscriberQuotas"
                label="Subscriber Quotas"
              />
              <MenuGroup
                link="/viewStatusSubscriberGroups"
                label="Subscriber Groups"
              />

              <MenuGroup label="RADIUS/REST/Billling">
                <MenuItem link="/viewRADIUSConf" label="RADIUS" />
                <MenuItem link="/viewRESTConf" label="REST API" />
                <MenuItem link="/viewBillingConf" label="Billing Systems" />
              </MenuGroup>

              <MenuGroup link="/viewDoSConf" label="DoS" />

              <MenuGroup
                link="/viewOptimizationConf"
                label="Optimization Settings"
              />
            </MenuGroup>
            <MenuGroup label="Administration" icon="build">
              <MenuGroup label="Backup">
                <MenuItem link="/viewBackupSave" label="Save Configuration" />
                <MenuItem link="/viewBackupLoad" label="Load Configuration" />
                <MenuItem
                  link="/viewSaveSubsAttributes"
                  label="Save External Attributes"
                />
                <MenuItem
                  link="/viewLoadSubsAttributes"
                  label="Load External Attributes"
                />
              </MenuGroup>

              <MenuGroup label="System Date">
                <MenuItem link="/viewSysNtp" label="NTP Servers" />
                <MenuItem link="/viewSysDate" label="Set Date & Time" />
              </MenuGroup>

              <MenuGroup label="Logs">
                <MenuItem link="/viewLogsSystem" label="System" />
                <MenuItem link="/viewLogsKernel" label="Kernel" />
              </MenuGroup>

              <MenuGroup label="Diagnostic" modal="diagnostic" />

              <MenuGroup link="/viewLicense" label="License" />

              <MenuGroup link="/viewSoftware" label="Software" />

              <MenuGroup link="/viewUsersAdmin" label="Users" />

              <MenuGroup
                link="/viewGeneralSettingsConfig"
                label="General Settings"
              />

              <MenuGroup link="/viewSNMPConf" label="SNMP" />

              <MenuGroup link="/viewClusterConfiguration" label="Cluster" />
              <MenuGroup label="Reboot" modal="reboot" />
              <MenuGroup label="Shutdown" modal="shutdown" />
            </MenuGroup>

            <MenuGroup
              label="AI Assistant"
              clickHandler={() => {
                setOpen((value) => !value);
              }}
              iconJsx={<AutoAwesomeIcon />}
            ></MenuGroup>
            <MenuGroup label="About" icon="info" modal="about"></MenuGroup>
          </ul>
        </div>
      </Aside>
      <BottomMenu className="bottom-menu">
        <Username />
      </BottomMenu>
    </>
  );
}
